import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { DEFAULT_PRODUCT_FIELDS, resolveProductFields } from "../utils/productFields";
import { db } from "../firebase";

const DEFAULT_PRODUCT_CATEGORIES = {
  Electronics: [
    "Mobile Phones",
    "Laptops & Computers",
    "Cameras",
    "Audio & Headphones",
    "Smart Home Devices",
    "Wearable Technology",
  ],
  "Fashion & Apparel": [
    "Men’s Clothing",
    "Women’s Clothing",
    "Kids’ Clothing",
    "Shoes",
    "Accessories (Bags, Jewelry, Watches)",
    "Sportswear",
  ],
  "Home & Living": [
    "Furniture",
    "Home Decor",
    "Kitchen & Dining",
    "Bedding & Bath",
    "Appliances",
  ],
  "Beauty & Health": [
    "Skincare",
    "Haircare",
    "Makeup",
    "Personal Care",
    "Health Supplements",
  ],
  "Sports & Outdoors": [
    "Fitness Equipment",
    "Outdoor Gear",
    "Sportswear",
    "Camping & Hiking",
  ],
  "Toys & Baby Products": [
    "Toys & Games",
    "Baby Clothing",
    "Baby Care",
    "Feeding & Nursing",
  ],
  Automotive: ["Car Accessories", "Motorbike Accessories", "Tools & Equipment"],
  "Books & Stationery": ["Books", "Office Supplies", "Art & Craft Supplies"],
  "Groceries & Gourmet Food": [
    "Fresh Produce",
    "Packaged Foods",
    "Beverages",
    "Organic & Health Foods",
  ],
  Pets: ["Pet Food", "Pet Accessories", "Pet Care"],
  Other: ["Other"],
};

const DEFAULT_SERVICE_CATEGORIES = {
  "Design & Creative": [
    "Logo Design",
    "Branding",
    "UI/UX",
    "Illustration",
    "Video Editing",
    "Photography",
  ],
  "Development & Tech": [
    "Web Development",
    "Mobile Apps",
    "Automation / Scripts",
    "WordPress / CMS",
    "AI Chatbots",
    "Blockchain / Web3",
  ],
  Marketing: [
    "Social Media Management",
    "Paid Ads Setup",
    "SEO",
    "Content Writing",
    "Email Marketing",
    "Analytics & Tagging",
  ],
  "Business & Consulting": [
    "Pitch Decks",
    "Market Research",
    "Business Strategy",
    "Process Automation",
  ],
  Education: ["Coaching", "Tutoring", "Course Creation", "Editing"],
  Other: ["Other"],
};

function toRows(source = {}) {
  return Object.entries(source).map(([name, subcategories], index) => ({
    id: `${name}-${index}`,
    name,
    subcategories: Array.isArray(subcategories)
      ? subcategories.join(", ")
      : String(subcategories || ""),
  }));
}

function toMap(rows = []) {
  return rows.reduce((acc, row) => {
    const name = String(row.name || "").trim();
    if (!name) return acc;

    const subcategories = [...new Set(
      String(row.subcategories || "")
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean),
    )];

    acc[name] = subcategories.length > 0 ? subcategories : ["Other"];
    return acc;
  }, {});
}

function CategorySection({
  title,
  description,
  rows,
  setRows,
}) {
  function updateRow(id, field, value) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  }

  function addRow() {
    setRows((current) => [
      ...current,
      {
        id: `new-${Date.now()}-${current.length}`,
        name: "",
        subcategories: "",
      },
    ]);
  }

  function removeRow(id) {
    setRows((current) => current.filter((row) => row.id !== id));
  }

  return (
    <section className="rounded-3xl border bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">{description}</p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="rounded-xl border px-4 py-2 font-semibold text-blue-700 hover:bg-blue-50"
        >
          Add category
        </button>
      </div>

      <div className="mt-5 space-y-4">
        {rows.map((row, index) => (
          <div
            key={row.id}
            className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[0.85fr,1.15fr,auto]"
          >
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Main category {index + 1}
              </label>
              <input
                value={row.name}
                onChange={(e) => updateRow(row.id, "name", e.target.value)}
                className="w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
                placeholder="Electronics"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Subcategories
              </label>
              <textarea
                rows={3}
                value={row.subcategories}
                onChange={(e) =>
                  updateRow(row.id, "subcategories", e.target.value)
                }
                className="w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
                placeholder="Mobile Phones, Laptops & Computers, Cameras"
              />
              <div className="mt-2 text-xs text-slate-500">
                Separate subcategories with commas or new lines.
              </div>
            </div>

            <div className="flex items-start justify-end pt-7">
              <button
                type="button"
                onClick={() => removeRow(row.id)}
                className="rounded-xl border border-rose-200 px-4 py-2 font-semibold text-rose-600 hover:bg-rose-50"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ListingCategorySettings() {
  const [productRows, setProductRows] = useState(() =>
    toRows(DEFAULT_PRODUCT_CATEGORIES),
  );
  const [serviceRows, setServiceRows] = useState(() =>
    toRows(DEFAULT_SERVICE_CATEGORIES),
  );
  const [fieldCategory, setFieldCategory] = useState("");
  const [productFields, setProductFields] = useState(DEFAULT_PRODUCT_FIELDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const ref = doc(db, "listing_settings", "defaults");
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        setProductFields(resolveProductFields(data));
        setProductRows(
          toRows(
            Object.keys(data.productCategories || {}).length
              ? data.productCategories
              : DEFAULT_PRODUCT_CATEGORIES,
          ),
        );
        setServiceRows(
          toRows(
            Object.keys(data.serviceCategories || {}).length
              ? data.serviceCategories
              : DEFAULT_SERVICE_CATEGORIES,
          ),
        );
        setLoading(false);
      },
      (snapshotError) => {
        console.error("listing category settings snapshot failed", snapshotError);
        setError("Failed to load listing categories.");
        setLoading(false);
      },
    );
  }, []);

  const productCount = useMemo(
    () => Object.keys(toMap(productRows)).length,
    [productRows],
  );
  const serviceCount = useMemo(
    () => Object.keys(toMap(serviceRows)).length,
    [serviceRows],
  );

  async function handleSave() {
    setSaving(true);
    setMessage("");
    setError("");

    try {
      if (productFields.some((f) => !f.name.trim())) throw new Error("Every product field needs a name.");
      if (productFields.some((f) => f.categories.some((c) => !toMap(productRows)[c]))) throw new Error("A product field uses a removed category. Update its categories before saving.");
      await setDoc(
        doc(db, "listing_settings", "defaults"),
        {
          productFields,
          productFieldsVersion: 2,
          productCategories: toMap(productRows),
          serviceCategories: toMap(serviceRows),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setMessage("Listing settings saved successfully.");
    } catch (saveError) {
      console.error(saveError);
      setError(saveError.message || "Failed to save listing settings.");
    } finally {
      setSaving(false);
    }
  }

  function resetDefaults() {
    setProductRows(toRows(DEFAULT_PRODUCT_CATEGORIES));
    setServiceRows(toRows(DEFAULT_SERVICE_CATEGORIES));
    setMessage("");
    setError("");
  }

  if (loading) {
    return <div className="px-2 py-6 text-sm text-slate-600">Loading categories...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
              Listing Settings
            </div>
            <h1 className="mt-2 text-3xl font-black text-slate-900">
              Manage categories and product fields
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              These categories power the instructor <code>/manage-listing</code>{" "}
              form. The current marketplace list is loaded as the default set, and
              you can change it any time from here.
            </p>
          </div>

          <div className="rounded-2xl border bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div>Product categories: {productCount}</div>
            <div>Service categories: {serviceCount}</div>
          </div>
        </div>

        {(message || error) && (
          <div
            className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {error || message}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`rounded-xl px-5 py-2.5 font-semibold text-white ${
              saving ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={resetDefaults}
            className="rounded-xl border px-5 py-2.5 font-semibold hover:bg-slate-50"
          >
            Reset categories to defaults
          </button>
        </div>
      </div>

      <CategorySection
        title="Product Categories"
        description="These main categories and subcategories will appear for product listings in the instructor portal."
        rows={productRows}
        setRows={setProductRows}
      />

      <section className="rounded-3xl border bg-white p-6 shadow-sm space-y-4">
        <h2 className="text-xl font-bold">Product fields</h2>
        <p className="text-sm text-slate-600">Choose the details sellers enter for each category. Leave categories unselected to apply a field to all products. Lists can describe sizes or colors; dates can record expiry dates.</p>
        <label className="block text-sm font-medium">Show fields for category
          <select className="mt-2 block w-full rounded-xl border p-3" value={fieldCategory} onChange={(e) => setFieldCategory(e.target.value)}><option value="">All categories</option>{Object.keys(toMap(productRows)).map((category) => <option key={category}>{category}</option>)}</select>
        </label>
        {productFields.filter((field) => !fieldCategory || !field.categories.length || field.categories.includes(fieldCategory)).map((field) => {
          const update = (patch) => setProductFields((rows) => rows.map((f) => f.id === field.id ? { ...f, ...patch } : f));
          return <div key={field.id} className="rounded-2xl border p-4 grid gap-3 md:grid-cols-2">
            <label>Field name<input className="block w-full border rounded-xl p-2" value={field.name} onChange={(e) => update({ name: e.target.value })} /></label>
            <label>Field type<select className="block w-full border rounded-xl p-2" value={field.type} onChange={(e) => update({ type: e.target.value })}>
              <option value="text">Text</option><option value="list">Multiple values (sizes, colors)</option><option value="number">Number</option><option value="date">Date</option>
            </select></label>
            <fieldset className="md:col-span-2"><legend className="mb-2">Applies to categories</legend><div className="flex flex-wrap gap-3">
              {Object.keys(toMap(productRows)).map((category) => <label key={category} className="text-sm"><input type="checkbox" checked={field.categories.includes(category)} onChange={(e) => update({ categories: e.target.checked ? [...field.categories, category] : field.categories.filter((c) => c !== category) })} /> {category}</label>)}
            </div></fieldset>
            {field.type === "list" && <label>Allowed values (optional, comma separated)<input className="block w-full border rounded-xl p-2" placeholder="S, M, L, XL" value={field.options || ""} onChange={(e) => update({ options: e.target.value })} /></label>}
            <div className="flex items-center gap-4">
              <label><input type="checkbox" checked={field.required} onChange={(e) => update({ required: e.target.checked })} /> Required</label>
              <label><input type="checkbox" checked={field.enabled !== false} onChange={(e) => update({ enabled: e.target.checked })} /> Enabled</label>
              <button type="button" className="text-red-700" onClick={() => setProductFields((rows) => rows.filter((f) => f.id !== field.id))}>Remove</button>
            </div>
          </div>;
        })}
        <button type="button" className="rounded-xl border px-4 py-2 text-blue-700" onClick={() => setProductFields((rows) => [...rows, { id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, name: "", type: "text", categories: [], required: false, enabled: true, options: "" }])}>Add product field</button>
        <p className="text-sm text-slate-500">Use Save Settings above to apply your changes. Existing saved product details are retained when a field is removed.</p>
      </section>

      <CategorySection
        title="Service Categories"
        description="These categories will appear for service listings in the instructor portal."
        rows={serviceRows}
        setRows={setServiceRows}
      />
    </div>
  );
}
